import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
// Components
import AutodiagFlowCard from './AutodiagFlowCard';
import AutodiagAssistantPreview from './AutodiagAssistantPreview';
import AutodiagFormPreview from './AutodiagFormPreview';
// Icons
import Check16 from '../icons/Check16';
import { AUTODIAG, TITLE_FONT } from './autodiagChoiceTokens';

// Écran de choix de l'étape autodiag (/reparation/autodiag) : assistant LLM
// ou formulaire Tally — refonte design FIX-430. Présentationnel : l'émission
// des événements d'exposition reste dans la scène RepairAutodiag (un seul
// endroit qui parle au réseau). La carte « Assistant » est volontairement
// mise en avant (choix produit assumé — à garder en tête à la lecture des
// taux de choix).
//
// Typo en styled-components locaux et non via l'atome Text : celui-ci code
// letter-spacing -0.01em en dur, incompatible avec les valeurs relevées sur
// le site live (titres -0.02em, corps normal — cf. autodiagChoiceTokens.js).
// AUCUN overflow:hidden sur la grille ni ses parents : le badge « Nouveau »
// de la carte assistant déborde au-dessus du bord.

const PageTitle = styled.h1`
  margin: 0;
  font-family: ${TITLE_FONT};
  /* !important : styles.less impose h1 { font-size: 2.5em !important } au
     niveau global (précédent assumé dans le repo, cf. RepairDefaultHomeAndShops).
     32px et non 36px : dans la colonne 66% du tunnel réel, le titre à 36px+
     passe sur deux lignes et coûte ~47px de pli — le critère dur est le
     titre sur UNE ligne. */
  font-size: 28px !important;
  /* 900 : seule face AronVF déclarée (Black) — cf. CardTitle. */
  font-weight: 900;
  letter-spacing: ${AUTODIAG.titleTracking};
  line-height: 1.15;
  color: ${AUTODIAG.dark};
  ${(p) => p.theme.mediaQueries.tablet} {
    font-size: 32px !important;
  }
`;

// Chapô + badge de couverture sur la même ligne (le chapô tient sur une
// ligne en desktop — gain de pli mesuré : 25px).
const SubRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-top: 8px;
`;

const Lede = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.4;
  letter-spacing: ${AUTODIAG.bodyTracking};
  color: ${AUTODIAG.dark};
`;

// Badge « Adresse couverte » — rendu UNIQUEMENT quand la zone est réellement
// couverte (prop addressCovered, branchée sur course.isInZone === true dans
// la scène). Jamais câblé en dur : une pill « 12 réparateurs à moins de
// 5 km » a déjà été retirée pour cette raison.
const CoverageBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border: 1px solid ${AUTODIAG.border};
  border-radius: 999px;
  background: ${AUTODIAG.white};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: ${AUTODIAG.bodyTracking};
  color: ${AUTODIAG.dark};
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  margin-top: ${AUTODIAG.gridMarginTopMobile}px;
  /* Côte à côte dès 768px (spec FIX-430) — le margin-top absorbe les 22px
     de descente du badge « Nouveau » dans la carte. */
  ${(p) => p.theme.mediaQueries.tablet} {
    flex-direction: row;
    margin-top: ${AUTODIAG.gridMarginTopDesktop}px;
  }
`;

export default function AutodiagFlowChoice({
  addressCovered,
  onPickLlm,
  onPickTally,
}) {
  const { t } = useTranslation();
  return (
    <div>
      <PageTitle>{t('autodiag.choice.title')}</PageTitle>
      <SubRow>
        <Lede>{t('autodiag.choice.subtitle')}</Lede>
        {addressCovered ? (
          <CoverageBadge data-testid="autodiag-coverage-badge">
            <Check16 color={AUTODIAG.dark} />
            {t('autodiag.choice.coverage.badge')}
          </CoverageBadge>
        ) : null}
      </SubRow>
      <Grid>
        <AutodiagFlowCard
          variant="primary"
          badge={t('autodiag.choice.llm.badge')}
          iconName="TCHAT"
          title={t('autodiag.choice.llm.title')}
          description={t('autodiag.choice.llm.description')}
          duration={t('autodiag.choice.llm.duration')}
          preview={<AutodiagAssistantPreview />}
          ctaLabel={t('autodiag.choice.cta')}
          ariaLabel={t('autodiag.choice.llm.aria')}
          onSelect={onPickLlm}
          testId="autodiag-choice-llm"
        />
        <AutodiagFlowCard
          variant="secondary"
          iconName="CARNET_ENTRETIEN"
          title={t('autodiag.choice.tally.title')}
          description={t('autodiag.choice.tally.description')}
          duration={t('autodiag.choice.tally.duration')}
          preview={<AutodiagFormPreview />}
          ctaLabel={t('autodiag.choice.cta')}
          ariaLabel={t('autodiag.choice.tally.aria')}
          onSelect={onPickTally}
          testId="autodiag-choice-tally"
        />
      </Grid>
    </div>
  );
}
